import { ensureFreshToken } from '../utils/tokenRefresh';
import apiMonitor from '../utils/apiMonitor';
import { 
  checkRateLimit, 
  getSecurityHeaders, 
  isPathSafe,
  maskSensitiveData 
} from '../utils/security';
import type { UserResponse, UpdateUserProfileRequest } from '../types/profile';
import { serverErrorMessage } from '../utils/serverError';

/** Détail de l'événement global 'auth:error' émis sur une réponse 401. */
export interface AuthErrorDetail {
  status: number;
  message?: string;
}

declare global {
  interface WindowEventMap {
    'auth:error': CustomEvent<AuthErrorDetail>;
  }
}

export interface ApiServiceError {
  status: number;
  message: string;
  errors?: Record<string, string[]>;
  code?: string; // Code d'erreur pour les cas spécifiques comme 'token_expired'
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';

// Système de cache pour éviter les requêtes répétées
interface CachedResponse<T> {
  data: T;
  timestamp: number;
}

// Cache d'une minute par défaut
const CACHE_TTL = 60 * 1000;

/**
 * Generic API service for handling HTTP requests.
 */
class ApiService {
  // Cache pour les requêtes GET
  private requestCache: Record<string, CachedResponse<unknown>> = {};
  
  // Fonction pour vérifier si une requête est récente
  private isCacheValid<T>(cacheKey: string): CachedResponse<T> | null {
    const cached = this.requestCache[cacheKey];
    if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
      console.log(`Utilisation du cache pour ${cacheKey}`);
      // Chaque entrée est indexée par l'URL complète : elle a le type demandé par l'appelant de get<T>
      return cached as CachedResponse<T>;
    }
    return null;
  }
  
  private async getAuthHeaders(isFormData = false): Promise<HeadersInit> {
    // Utiliser ensureFreshToken pour garantir un token valide
    const token = await ensureFreshToken();
    const headers: HeadersInit = {};

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    headers['Accept'] = 'application/json';

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // En-têtes de cache supprimés pour éviter les problèmes CORS
    // Le serveur n'autorise pas ces en-têtes dans les requêtes préliminaires

    // Ajouter des headers pour le suivi et le debugging
    headers['X-Client-Version'] = '1.0.0';
    headers['X-Request-Time'] = new Date().toISOString();
    
    // 🔒 Ajouter les headers de sécurité (CSRF, etc.)
    const securityHeaders = getSecurityHeaders();
    Object.assign(headers, securityHeaders);

    return headers;
  }
  
  /**
   * 🔒 Valide un endpoint avant d'effectuer la requête
   * Protection contre Path Traversal et injection
   */
  private validateEndpoint(endpoint: string): boolean {
    if (!endpoint || typeof endpoint !== 'string') {
      console.error('🚨 Endpoint invalide');
      return false;
    }
    
    // Vérifier les tentatives de path traversal
    if (!isPathSafe(endpoint)) {
      console.error('🚨 Path traversal détecté dans l\'endpoint:', endpoint);
      return false;
    }
    
    // Vérifier les caractères dangereux
    if (/[<>"'`\\]/.test(endpoint)) {
      console.error('🚨 Caractères dangereux dans l\'endpoint:', endpoint);
      return false;
    }
    
    return true;
  }
  
  /**
   * 🔒 Vérifie le rate limit avant une requête
   */
  private checkApiRateLimit(endpoint: string, method: string): void {
    const { allowed, retryAfter } = checkRateLimit('apiCall', `${method}:${endpoint}`);
    
    if (!allowed) {
      const error: ApiServiceError = {
        status: 429,
        message: `Trop de requêtes. Réessayez dans ${retryAfter} secondes.`,
        code: 'rate_limit_exceeded'
      };
      throw error;
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorData: ApiServiceError;
      try {
        const res = await response.json();
        errorData = {
          status: response.status,
          // Liste de validation et raison d'un refus enveloppé comprises :
          // c'est ce message que l'écran affiche tel quel.
          message: serverErrorMessage(res, 'An unknown error occurred.'),
          errors: res.errors,
          code: res.code
        };
        
        // Détection spécifique des erreurs d'authentification
        if (response.status === 401) {
          console.warn('Erreur d\'authentification détectée:', errorData.message);
          
          // ⚠️ Ne déclencher auth:error QUE si aucun refresh n'est en cours.
          // Si un refresh est en cours, le hook useUser gère lui-même le retry —
          // dispatcher ici en même temps provoquerait un logout loop.
          const refreshInProgress = localStorage.getItem('token_refresh_in_progress') === 'true';
          const justLoggedIn = sessionStorage.getItem('auth0_just_logged_in') === 'true';
          if (!refreshInProgress && !justLoggedIn) {
            const authErrorEvent = new CustomEvent<AuthErrorDetail>('auth:error', { 
              detail: { 
                status: 401, 
                message: errorData.message 
              } 
            });
            window.dispatchEvent(authErrorEvent);
          } else {
            console.log('🔄 401 ignoré par auth:error — refresh en cours ou login récent');
          }
        }
      } catch (e) {
        errorData = { 
          status: response.status, 
          message: response.statusText,
          code: `http_${response.status}`
        };
      }
      throw errorData;
    }

    if (response.status === 204) { // No Content
      return {} as T;
    }

    try {
      return await response.json();
    } catch (e) {
      console.error('Erreur lors du parsing de la réponse JSON:', e);
      throw {
        status: 0,
        message: 'Error parsing server response',
        code: 'json_parse_error'
      };
    }
  }

  // `object` (et non Record<string, unknown>) pour accepter les interfaces de filtres, qui n'ont pas de signature d'index
  async get<T>(endpoint: string, params?: object, forceRefresh = false): Promise<T> {
    // 🔒 Valider l'endpoint
    if (!this.validateEndpoint(endpoint)) {
      throw { status: 400, message: 'Endpoint invalide', code: 'invalid_endpoint' } as ApiServiceError;
    }
    
    // 🔒 Vérifier le rate limit
    this.checkApiRateLimit(endpoint, 'GET');
    
    const url = new URL(`${API_BASE_URL}${endpoint}`);
    if (params) {
      const query = params as Record<string, unknown>;
      Object.keys(query).forEach(key => url.searchParams.append(key, String(query[key])));
    }
    
    // Clé de cache basée sur l'URL complète avec les paramètres
    const cacheKey = url.toString();
    
    // Démarrer le monitoring de la requête
    const requestId = apiMonitor.startRequest(endpoint, 'GET');
    
    try {
      // Utiliser le cache si disponible et si on ne force pas le rafraîchissement
      if (!forceRefresh) {
        const cached = this.isCacheValid<T>(cacheKey);
        if (cached) {
          apiMonitor.endRequest(requestId, 200, true);
          return cached.data;
        }
      }
      
      // Add a cache-busting query parameter if forceRefresh is true
      if (forceRefresh) {
        url.searchParams.append('_t', Date.now().toString());
      }
      
      // Obtenir les headers de façon asynchrone
      const headers = await this.getAuthHeaders(false);
      
      try {
        // Vérifie d'abord si le serveur est accessible
        const timeout = 3000; // 3 secondes de timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        try {
          const response = await fetch(url.toString(), {
            method: 'GET',
            headers,
            signal: controller.signal,
            // Skip cache when forcing a refresh
            cache: forceRefresh ? 'no-store' : 'default',
          });
          
          clearTimeout(timeoutId);
          
          const data = await this.handleResponse<T>(response);
          
          // Mettre en cache la réponse sauf si on a forcé le rafraîchissement
          if (!forceRefresh) {
            this.requestCache[cacheKey] = {
              data,
              timestamp: Date.now()
            };
          }
          
          // Terminer le monitoring de la requête
          apiMonitor.endRequest(requestId, response.status, response.ok);
          
          return data;
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      } catch (fetchError) {
        // Enregistrer l'erreur
        const errorMessage = fetchError instanceof Error ? fetchError.message : 'Erreur inconnue';
        const status = 500;
        apiMonitor.endRequest(requestId, status, false, errorMessage);
        
        // Gérer spécifiquement les erreurs CORS et de connexion
        if (fetchError instanceof TypeError && fetchError.message === 'Failed to fetch') {
          console.error('Erreur CORS ou réseau détectée:', fetchError);
          // Vérifier si le serveur est en cours d'exécution
          console.warn(`Vérifiez que le serveur backend est en cours d'exécution sur ${API_BASE_URL}`);
        } else if (fetchError instanceof Error && fetchError.name === 'AbortError') {
          console.error('Le serveur ne répond pas (timeout):', url.toString());
        }
        throw fetchError;
      }
    } catch (error) {
      // Enregistrer l'erreur dans le monitoring
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const status = (error as ApiServiceError)?.status || 500;
      apiMonitor.endRequest(requestId, status, false, errorMessage);
      throw error;
    }
  }

  async post<T>(endpoint: string, data: unknown): Promise<T> {
    // 🔒 Valider l'endpoint
    if (!this.validateEndpoint(endpoint)) {
      throw { status: 400, message: 'Endpoint invalide', code: 'invalid_endpoint' } as ApiServiceError;
    }
    
    // 🔒 Vérifier le rate limit (plus strict pour POST)
    const { allowed, retryAfter } = checkRateLimit('formSubmit', endpoint);
    if (!allowed) {
      throw { 
        status: 429, 
        message: `Trop de requêtes. Réessayez dans ${retryAfter} secondes.`,
        code: 'rate_limit_exceeded' 
      } as ApiServiceError;
    }
    
    // Démarrer le monitoring
    const requestId = apiMonitor.startRequest(endpoint, 'POST');
    
    // ✨ DEBUG: Log des données envoyées (masquer les données sensibles)
    console.log(`🚀 API POST ${endpoint}:`, JSON.stringify(maskSensitiveData(data as Record<string, unknown>), null, 2));
    
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      
      const responseData = await this.handleResponse<T>(response);
      
      // Terminer le monitoring
      apiMonitor.endRequest(requestId, response.status, response.ok);
      
      return responseData;
    } catch (error) {
      // Enregistrer l'erreur
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const status = (error as ApiServiceError)?.status || 500;
      apiMonitor.endRequest(requestId, status, false, errorMessage);
      throw error;
    }
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers,
    });
    return this.handleResponse<T>(response);
  }

  async upload<T>(endpoint: string, file: File, fieldName = 'file'): Promise<T> {
    const formData = new FormData();
    formData.append(fieldName, file);

    const headers = await this.getAuthHeaders(true); // isFormData = true
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    return this.handleResponse<T>(response);
  }

  async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    const headers = await this.getAuthHeaders(true); // Pass true to indicate FormData
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return this.handleResponse<T>(response);
  }

  // User-specific API methods
  async getUserProfile(forceRefresh = false): Promise<UserResponse> {
    return this.get<UserResponse>('/users/me', undefined, forceRefresh);
  }

  async updateUserProfile(data: UpdateUserProfileRequest): Promise<UserResponse> {
    return this.patch<UserResponse>('/users/me', data);
  }

  async getUserSettings(): Promise<unknown> {
    return this.get<unknown>('/users/me/settings');
  }

  async getAppData(appName: string): Promise<unknown> {
    return this.get<unknown>(`/apps/${appName}/data`);
  }
}

// Export a singleton instance of the ApiService
const api = new ApiService();
export const apiService = api;
export default api;
